import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { FOCUSABLE_SELECTOR, handleTrappedTabKey, isReachable } from 'core/utils/keyboardNavigation';

type UseLayoutCalendarMobileMenuParams = {
  blockClass?: string;
  isOpen: boolean;
  isResponsive: boolean;
  onToggle?: () => void;
};

type UseLayoutCalendarMobileMenuReturn = {
  asideId: string;
  asideRef: RefObject<HTMLElement>;
  closeButtonRef: RefObject<HTMLButtonElement>;
  isDrawerOpen: boolean;
  menuButtonRef: RefObject<HTMLButtonElement>;
  rootRef: RefObject<HTMLDivElement>;
};

// Hermanos del drawer que salen del orden de Tab y del árbol de accesibilidad mientras el drawer
// los cubre. Se buscan por clase desde la raíz para que el layout no necesite refs extra y queden
// cubiertos también la cabecera y el pie que pasa el consumidor. La clase de bloque es parámetro
// porque LayoutTwoColumns comparte este contrato con LayoutCalendar y sus elementos solo difieren
// en el bloque (TAN-10964).
function getInertTargetsSelector(blockClass: string): string {
  return `.${blockClass}__header, .header-responsive, .${blockClass}__column-2, .${blockClass}__footer`;
}

// Mirrors `$breakpoints-xlarge` (_breakpoints.scss): from here on the aside is a fixed sidebar,
// the hamburger and the close button are `display: none` and there is no drawer to manage.
const DESKTOP_MEDIA_QUERY = '(min-width: 1280px)';

let asideIdCounter = 0;

export function useIsDesktopViewport(): boolean {
  const [isDesktop, setIsDesktop] = useState(() => Boolean(globalThis.matchMedia?.(DESKTOP_MEDIA_QUERY).matches));

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia?.(DESKTOP_MEDIA_QUERY);
    if (!mediaQuery) return undefined;

    function onChange(event: MediaQueryListEvent): void {
      setIsDesktop(event.matches);
    }

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

// A control inside the drawer that is expanded (SelectCustomizable puts `aria-expanded` on its
// combobox input, which is the event target) owns Escape: closing the drawer instead would leave
// its listbox open, and cutting the propagation would stop React from ever delivering the key.
export function isEscapeOwnedByDrawerControl(target: EventTarget | null, aside: HTMLElement | null): boolean {
  if (!(target instanceof Element) || !aside?.contains(target)) return false;
  return target.closest('[aria-expanded="true"]') !== null;
}

/**
 * Contrato de teclado y foco del drawer responsive que comparten LayoutCalendar y LayoutTwoColumns
 * cuando se pintan con `responsive` (mismo enfoque que useStudentGradesMobileMenu, TAN-10396):
 * Escape lo cierra, el foco va al botón de cerrar al abrir y vuelve a la hamburguesa al cerrar, el
 * contenido de detrás queda `inert` mientras está abierto (con un Tab atrapado a mano como respaldo
 * para navegadores sin `inert`), el propio panel queda `inert` mientras está cerrado (con un
 * respaldo manual del mismo tipo) y el bloqueo del scroll del body vive aquí y no en cada vista.
 *
 * Above the desktop breakpoint the aside is a plain sidebar, so the drawer is forced closed
 * there regardless of the consumer's state: crossing the breakpoint with it open must release
 * the `inert`, the scroll lock and the trap.
 */
function useLayoutCalendarMobileMenu({
  blockClass = 'layout-calendar',
  isOpen,
  isResponsive,
  onToggle,
}: UseLayoutCalendarMobileMenuParams): UseLayoutCalendarMobileMenuReturn {
  const asideRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const asideIdRef = useRef<string | null>(null);
  const isFirstRunRef = useRef(true);
  const isDesktop = useIsDesktopViewport();
  const isDrawerOpen = isOpen && !isDesktop;
  // `isResponsive` is what tells an off-canvas closed drawer apart from a permanent sidebar.
  const isDrawerCollapsed = isResponsive && !isDesktop && !isDrawerOpen;

  if (asideIdRef.current === null) {
    asideIdCounter += 1;
    asideIdRef.current = `${blockClass}-aside-${asideIdCounter}`;
  }

  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  // `inert` also hides the covered content from the accessibility tree, which the Tab trap
  // below cannot do. Set via attribute because React 17 typings don't know the prop.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>(getInertTargetsSelector(blockClass)).forEach((element) => {
      if (isDrawerOpen) {
        element.setAttribute('inert', '');
      } else {
        element.removeAttribute('inert');
      }
    });
  }, [blockClass, isDrawerOpen]);

  // The `visibility: hidden` of the closed drawer does not close its tab order on its own:
  // visibility is inherited, and react-select re-declares `visibility: visible` on its Input
  // wrapper (inputCSS, react-select 3.1.0), leaving a phantom tab stop on the SelectCustomizable
  // of an off-canvas panel. Declared before the focus effect so opening releases `inert` first.
  useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return;
    if (isDrawerCollapsed) {
      aside.setAttribute('inert', '');
    } else {
      aside.removeAttribute('inert');
    }
  }, [isDrawerCollapsed]);

  // Fallback for browsers that ignore `inert`, the mirror image of the Tab trap that covers the
  // open state: Tab walks the page as if the panel were not there. It intercepts the key instead
  // of rewriting `tabindex` because the panel can hold a roving-tabindex widget (the student
  // Pending/Delivered tablist, TAN-10525) whose attributes belong to that widget.
  useEffect(() => {
    if (!isDrawerCollapsed) return undefined;

    function onKeyDown(event: KeyboardEvent): void {
      const aside = asideRef.current;
      if (event.key !== 'Tab' || !aside) return;

      // `isReachable` already drops whatever the CSS hid, so the only panel controls left here are
      // the ones that opted out of the inherited `visibility: hidden` — the case being fixed.
      const reachable = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isReachable);
      const active = document.activeElement;
      // Only Tab coming from outside is redirected. Focus cannot reach the panel while collapsed,
      // and if it somehow starts there its Tab is left alone rather than trapped.
      if (active instanceof Element && aside.contains(active)) return;

      const from = active instanceof HTMLElement ? reachable.indexOf(active) : -1;
      const step = event.shiftKey ? -1 : 1;

      let index = from + step;
      if (!reachable[index] || !aside.contains(reachable[index])) return;
      while (reachable[index] && aside.contains(reachable[index])) index += step;

      const target = reachable[index];
      if (!target) return;

      event.preventDefault();
      target.focus();
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isDrawerCollapsed]);

  // Lock the body scroll only while open, restoring whatever value was there before:
  // the layout can mount under someone else's lock (DialogSearchQuestionsNew renders it
  // inside a Dialog) and must not clear it. Cleanup also covers unmounting while open.
  useEffect(() => {
    if (!isDrawerOpen) return undefined;
    const previousOverflowY = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = previousOverflowY;
    };
  }, [isDrawerOpen]);

  // Focus follows the open/close transition regardless of how it was triggered (X, backdrop,
  // Escape or a programmatic close from the consumer). Mounting already open also focuses the
  // drawer (DialogSearchQuestionsNew keeps `showMobileMenu` across reopenings); mounting closed
  // must not steal focus from the page.
  useEffect(() => {
    const isFirstRun = isFirstRunRef.current;
    isFirstRunRef.current = false;
    if (isDrawerOpen) {
      closeButtonRef.current?.focus();
    } else if (!isFirstRun) {
      menuButtonRef.current?.focus();
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    // The drawer holds the only reachable controls while open, in DOM order. The backdrop is
    // `tabindex="-1"` (mouse-only affordance, the X is its keyboard counterpart).
    function getTrappedElements(): HTMLElement[] {
      const aside = asideRef.current;
      if (!aside) return [];
      return Array.from(aside.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isReachable);
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        if (isEscapeOwnedByDrawerControl(event.target, asideRef.current)) return;

        // Capture phase + stopPropagation: when the layout lives inside a Dialog
        // (DialogSearchQuestionsNew), Escape must close only the drawer, never both.
        event.stopPropagation();
        onToggleRef.current?.();
        return;
      }

      if (event.key !== 'Tab') return;

      handleTrappedTabKey(event, getTrappedElements());
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isDrawerOpen]);

  return { asideId: asideIdRef.current, asideRef, closeButtonRef, isDrawerOpen, menuButtonRef, rootRef };
}

export default useLayoutCalendarMobileMenu;
